import { HttpClient } from '@angular/common/http';
import {
	APP_INITIALIZER,
	DoBootstrap,
	Inject,
	Injector,
	NgModule,
} from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { BrowserModule } from '@angular/platform-browser';
import { UpgradeModule } from '@angular/upgrade/static';
import { WINDOW } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { EffectsModule } from '@ngrx/effects';
import { ActionReducerMap, combineReducers, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { firstValueFrom } from 'rxjs';

import { environment } from '../environments/environment';
import { initApp } from './ajs-init.provider';
import { AjsInjectorService } from './ajs-injector.service';
import { AjsScopeService } from './ajs-scope.service';
import { AjsStateService } from './ajs-state.service';
import { ChartModule } from './chart/chart.module';
import { ConnectorModule } from './connector/connector.module';
import { CoreModule } from './core/core.module';
import {
	AppEffects,
	AppSelector,
	CoreFeatureKey,
	coreReducers,
	IAppRootState,
	TaskEffects,
	TaskSelector,
} from './core/store';
import { DashboardModule } from './dashboard/dashboard.module';
import { DatablockModule } from './datablock/datablock.module';
import { DepotModule } from './depot/depot.module';
import { EntityModule } from './entity/entity.module';
import { ExportTemplateModule } from './export-template/export-template.module';
import { ExpositionsModule } from './expositions/expositions.module';
import { FormulaModule } from './formula/formula.module';
import { GridModule } from './grid/grid.module';
import { MapModule } from './map/map.module';
import { MediaModule } from './media/media.module';
import { MetadataModule } from './metadata/metadata.module';
import { OrphanProjectModule } from './orphan-project/orphan-project.module';
import { PermissionModule } from './permission/permission.module';
import { RankingModule } from './ranking/ranking.module';
import { RequestersModule } from './requesters';
import { SemanticRuleModule } from './semantic-rule/semantic-rule.module';
import { TimelineModule } from './timeline/timeline.module';
import { TraceModule } from './trace/trace.module';
import { SplashComponent } from './ui/components/splash/splash.component';
import {
	ActionIconsInjectable,
	DcIconsInjectable,
	GdIconsInjectable,
	HdIconsInjectable,
	UiComponentIconsInjectable,
} from './ui/ui.module';

const miscellaneousModules = [
	ExportTemplateModule,
	FormulaModule,
	RankingModule,
	SemanticRuleModule,
	MetadataModule,
	TraceModule,
];
const administrationModules = [OrphanProjectModule];

const handleModules = [
	ExpositionsModule,
	DashboardModule,
	GridModule,
	MapModule,
	TimelineModule,
	MediaModule,
	ChartModule,
];
const genericsModules = [
	DatablockModule,
	EntityModule,
	DepotModule,
	ConnectorModule,
	PermissionModule,
];

export const rootReducer: ActionReducerMap<IAppRootState> = {
	[CoreFeatureKey]: combineReducers(coreReducers),
};

export const APP_INIT =
	(
		http: HttpClient,
		ajsScopeService: AjsScopeService,
		window: Window,
		upgrade: UpgradeModule
	): (() => Promise<unknown>) =>
	async () => {
		if (window.location.hash.startsWith('#/')) {
			location.href = window.location.hash.replace('#/', '');
			return;
		}
		const isAuthenticated = await firstValueFrom(
			http.get('authenticated', {
				responseType: 'text' as const,
			})
		);
		const logged = !!isAuthenticated;
		if (!logged) {
			return Promise.resolve(false);
		} else {
			await initApp(upgrade);
			return Promise.resolve(true);
		}
	};

@NgModule({
	imports: [
		...miscellaneousModules,
		...administrationModules,
		...handleModules,
		...genericsModules,
		BrowserModule,
		UpgradeModule,
		CoreModule,
		RequestersModule,
		StoreModule.forRoot(rootReducer),
		EffectsModule.forRoot([AppEffects, TaskEffects]),
		StoreDevtoolsModule.instrument({
			maxAge: 20,
			logOnly: environment.production,
		}),
	],
	providers: [
		AjsInjectorService,
		AjsScopeService,
		AjsStateService,
		AppSelector,
		TaskSelector,
		AppModalService,
		{
			provide: APP_INITIALIZER,
			useFactory: APP_INIT,
			deps: [HttpClient, AjsScopeService, WINDOW, UpgradeModule],
			multi: true,
		},
	],
	declarations: [],
})
export class AppModule implements DoBootstrap {
	public constructor(
		@Inject(DcIconsInjectable) private readonly dcIcons: AppIcons,
		@Inject(GdIconsInjectable) private readonly gdIcons: AppIcons,
		@Inject(HdIconsInjectable) private readonly hdIcons: AppIcons,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons,
		@Inject(UiComponentIconsInjectable) private readonly uiCmpIcons: AppIcons,
		private readonly injector: Injector
	) {
		this.dcIcons.addIconsToRegistry();
		this.gdIcons.addIconsToRegistry();
		this.hdIcons.addIconsToRegistry();
		this.actionIcons.addIconsToRegistry();
		this.uiCmpIcons.addIconsToRegistry();

		const splashComponentCmpElement = createCustomElement(SplashComponent, {
			injector,
		});
		customElements.define('app-splash', splashComponentCmpElement);
	}

	public ngDoBootstrap(): void {
		console.warn('bootstrapping root module...');
	}
}
